import { inject, Injectable } from '@angular/core';
import { TranslateLoader, TranslationObject } from '@ngx-translate/core';
import { from, Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { CHUNK_LOAD_RECOVERY } from './chunk-load-recovery.token';
import { isChunkLoadError } from './chunk-load-recovery';
import {
  isSupportedTranslationLang,
  TRANSLATION_LOADERS,
} from './translation-loaders';

function toTranslationObject(
  module: { default: TranslationObject },
): TranslationObject {
  return module.default;
}

@Injectable()
export class LazyTranslateLoader implements TranslateLoader {
  private readonly chunkLoadRecovery = inject(CHUNK_LOAD_RECOVERY, {
    optional: true,
  });

  /** Deduplicates concurrent requests for the same locale (avoids duplicate reload triggers). */
  private readonly translationCache = new Map<
    string,
    Observable<TranslationObject>
  >();

  getTranslation(lang: string): Observable<TranslationObject> {
    if (!isSupportedTranslationLang(lang)) {
      return of({});
    }

    const cached = this.translationCache.get(lang);
    if (cached) {
      return cached;
    }

    const translation$ = from(TRANSLATION_LOADERS[lang]()).pipe(
      map((module) => {
        this.chunkLoadRecovery?.clearReloadMarker();
        return toTranslationObject(module);
      }),
      catchError((error: unknown) => {
        if (isChunkLoadError(error)) {
          this.chunkLoadRecovery?.handleChunkLoadFailure();
        }

        return of({} as TranslationObject);
      }),
      shareReplay(1),
    );

    this.translationCache.set(lang, translation$);
    return translation$;
  }
}
